import {z} from "zod/v4-mini"
import {
    registerTemplateGraphNode,
    MaterialAssignment,
    materialAssignmentLikeMarker,
    MaterialInput,
    materialLike,
    getTemplateNodeReturns,
    materialSide,
    numberLike,
    TypeDescriptors,
} from "@cm/template-nodes"
import type {
    MaterialAssignmentConstructorParameters,
    MaterialAssignmentParameters,
    MaterialAssignmentLikeNode,
    TemplateNodeDefinition,
    MaterialLikeNode,
    MaterialSide,
    NumberLike,
    EvaluableTemplateNode,
    BuilderInlet,
    GraphBuilderScope,
    NodeEvaluator,
} from "@cm/template-nodes"
import {MattressNode} from "#mattress/nodes/mattress-node"
import type {MattressMaterialAssignmentData} from "#mattress/nodes/component/mattress-component-types"
import {uuid4} from "@cm/utils"

const materialDataValueDesc = TypeDescriptors.Nullable(TypeDescriptors.MaterialGraph)

const definition: TemplateNodeDefinition<MaterialAssignmentParameters, MaterialAssignmentConstructorParameters> = {
    nodeClass: "MattressMaterialAssignment",
    validation: {
        parameters: z.object({
            node: z.nullable(materialLike),
            horizontalOffset: z.optional(numberLike),
            verticalOffset: z.optional(numberLike),
            rotation: z.optional(numberLike),
            side: materialSide,
            alphaHashing: z._default(z.boolean(), false),
        }),
        returns: getTemplateNodeReturns(),
    },
}

@registerTemplateGraphNode
export class MattressMaterialAssignment
    extends MattressNode<MaterialAssignmentParameters, MaterialAssignmentConstructorParameters>
    implements EvaluableTemplateNode<MattressMaterialAssignmentData | null>, MaterialAssignmentLikeNode
{
    get [materialAssignmentLikeMarker]() {
        return true as const
    }

    public override get definition() {
        return definition
    }

    evaluate(scope: GraphBuilderScope, evaluator: NodeEvaluator): BuilderInlet<MattressMaterialAssignmentData | null> {
        const {node, horizontalOffset, verticalOffset, rotation, side} = this.parameters

        const localMaterialInput = scope.const(() => new MaterialInput({parameters: {name: "", id: uuid4()}}), "localMaterialInputSrc")
        const localNodeSrc = scope.const(
            () =>
                new MaterialAssignment({
                    parameters: {
                        node: null,
                        side: "front",
                    },
                }),
            "localNodeSrc",
        )

        const localNode = scope.pureLambda(
            scope.tuple(localNodeSrc, localMaterialInput, horizontalOffset, verticalOffset, rotation, side),
            ([localNodeSrc, localMaterialInput, horizontalOffset, verticalOffset, rotation, side]) => {
                return localNodeSrc.clone({
                    cloneInstanceIds: true,
                    cloneSubNode: () => false,
                    parameterOverrides: {
                        node: localMaterialInput,
                        horizontalOffset: horizontalOffset ?? undefined,
                        verticalOffset: verticalOffset ?? undefined,
                        rotation: rotation ?? undefined,
                        side,
                    },
                })
            },
            "localNode",
        )

        return scope.struct<MattressMaterialAssignmentData>("mattressMaterialAssignmentData", {
            localNode,
            localMaterialInput,
            sourceNode: node,
            materialData: node ? scope.value(evaluator.evaluate(node), materialDataValueDesc, "materialData") : null,
        })
    }
}

export type {MaterialAssignmentParameters, MaterialLikeNode, MaterialSide, NumberLike}
